/**
 * Email verification (shadcn, RM-117). Testids:
 * verify-code-input, verify-submit, resend-code, go-to-login. Resend success
 * text "Kod wyslany ponownie"; success banner "Email zweryfikowany!".
 */
import { useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { AuthShell, NoticeBanner } from '../components/Auth'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { verifyEmail, resendVerification } from '../lib/endpoints/auth'

export function VerifyEmailPage() {
  const { email } = useParams({ from: '/verify-email/$email' })
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)
  const [resendMsg, setResendMsg] = useState<string | null>(null)
  const nav = useNavigate()

  const onVerify = async () => {
    const trimmed = code.trim()
    if (!trimmed) {
      setError('Wpisz kod weryfikacyjny')
      return
    }
    setError(null)
    setLoading(true)
    try {
      await verifyEmail(email, trimmed)
      setSuccess(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Blad weryfikacji')
    } finally {
      setLoading(false)
    }
  }

  const onResend = async () => {
    setResendMsg(null)
    setResendLoading(true)
    try {
      await resendVerification(email)
      setResendMsg('Kod wyslany ponownie')
    } catch (e) {
      setResendMsg(e instanceof Error ? e.message : 'Blad wysylki')
    } finally {
      setResendLoading(false)
    }
  }

  return (
    <AuthShell>
      <h1 className="text-2xl font-bold text-center mb-2 text-foreground">
        Weryfikacja email
      </h1>
      <p className="text-center text-sm text-muted-foreground mb-6">
        Wyslalismy kod weryfikacyjny na{' '}
        <span className="font-medium text-foreground">{email}</span>
      </p>

      {success ? (
        <>
          <NoticeBanner
            message="Email zweryfikowany! Mozesz sie zalogowac."
            tone="success"
            centered
          />
          <Button
            className="w-full"
            size="lg"
            data-testid="go-to-login"
            onClick={() => void nav({ to: '/login', replace: true })}
          >
            Przejdz do logowania
          </Button>
        </>
      ) : (
        <>
          {error && <NoticeBanner message={error} tone="error" />}
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Kod weryfikacyjny</Label>
              <Input
                className="text-center text-lg tracking-widest"
                type="text"
                placeholder="123456"
                value={code}
                maxLength={10}
                autoComplete="one-time-code"
                data-testid="verify-code-input"
                onChange={(e) => setCode(e.target.value)}
              />
            </div>
            <Button
              className="w-full"
              size="lg"
              data-testid="verify-submit"
              disabled={loading}
              onClick={onVerify}
            >
              {loading ? 'Weryfikacja...' : 'Zweryfikuj'}
            </Button>

            <div className="text-center text-sm text-muted-foreground">
              {resendMsg && <p className="mb-1">{resendMsg}</p>}
              Nie dostales kodu?{' '}
              <Button
                variant="link"
                className="h-auto p-0"
                data-testid="resend-code"
                disabled={resendLoading}
                onClick={onResend}
              >
                {resendLoading ? 'Wysylanie...' : 'Wyslij ponownie'}
              </Button>
            </div>

            <p className="text-center text-sm text-muted-foreground mt-2">
              <Button
                variant="link"
                className="h-auto p-0"
                onClick={() => void nav({ to: '/login', replace: true })}
              >
                Wroc do logowania
              </Button>
            </p>
          </div>
        </>
      )}
    </AuthShell>
  )
}
